import React, { createContext, useContext, useState, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

export interface DragItem {
  type: 'schema-field' | 'variable';
  nodeId: string;
  nodeName: string;
  path: string;
  fieldType: string;
  value?: any;
  sourceHandle?: string;
  label?: string;
  /**
   * When true, this drag item represents the "current item" in an iterating node.
   * Expression should generate $node["X"].output.item.field instead of $node["X"].output[0].field
   */
  isVirtualItem?: boolean;
}

export interface DropResult {
  parameterId: string;
  parameterName: string;
  expression: string;
}

interface DragDropContextValue {
  isDragging: boolean;
  dragItem: DragItem | null;
  setDragItem: (item: DragItem | null) => void;
  setIsDragging: (dragging: boolean) => void;
  onDrop: (result: DropResult) => void;
  registerDropHandler: (handler: (result: DropResult) => void) => void;
}

const DragDropContext = createContext<DragDropContextValue | undefined>(undefined);

export const useDragDrop = () => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error('useDragDrop must be used within DragDropProvider');
  }
  return context;
};

interface DragDropProviderProps {
  children: React.ReactNode;
}

export const DragDropProvider: React.FC<DragDropProviderProps> = ({ children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragItem, setDragItem] = useState<DragItem | null>(null);
  const [dropHandler, setDropHandler] = useState<((result: DropResult) => void) | null>(null);

  const handleDrop = useCallback((result: DropResult) => {
    if (dropHandler) {
      dropHandler(result);
    }
    setIsDragging(false);
    setDragItem(null);
  }, [dropHandler]);

  const registerDropHandler = useCallback((handler: (result: DropResult) => void) => {
    setDropHandler(() => handler);
  }, []);

  const value: DragDropContextValue = {
    isDragging,
    dragItem,
    setDragItem,
    setIsDragging,
    onDrop: handleDrop,
    registerDropHandler,
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <DragDropContext.Provider value={value}>
        {children}
      </DragDropContext.Provider>
    </DndProvider>
  );
};
